import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memberProfileAPI, ClinicianService, ClinicianServiceInput } from '../../services/api';
import Button from '../ui/Button';
import { Input } from '../ui/FormField';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

interface ServicesEditorProps {
  editing: boolean;
}

// 15-min increments to match the booking grid (the booking layer enforces this).
const DURATION_OPTIONS = [30, 45, 60, 75, 90, 120];
/** "$300", "300,00" or " 300 " all become "300.00"; blank stays null. */
const normalizePrice = (raw: number | string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw).replace(/,/g, '.').replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n.toFixed(2) : null;
};

const EMPTY_DRAFT: ClinicianServiceInput = { name: '', duration_minutes: 60, capacity: 1, price: '', description: '' };

const formatPrice = (p?: number | string | null): string | null => {
  if (p === null || p === undefined || p === '') return null;
  const n = Number(p);
  return Number.isNaN(n) ? null : `$${n.toFixed(0)}`;
};

/**
 * Self-contained editor for a clinician's service offerings (Phase 3d).
 * Services save immediately via their own CRUD endpoints (independent of the
 * profile form's Save/Cancel). Shows read-only cards unless `editing`.
 */
const ServicesEditor: React.FC<ServicesEditorProps> = ({ editing }) => {
  const [services, setServices] = useState<ClinicianService[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<ClinicianServiceInput>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setServices(await memberProfileAPI.listServices());
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!draft.name.trim()) {
      toast.error('Service name is required');
      return;
    }
    setAdding(true);
    try {
      const created = await memberProfileAPI.createService({
        ...draft,
        price: normalizePrice(draft.price),
      });
      setServices((prev) => [...prev, created]);
      setDraft(EMPTY_DRAFT);
      toast.success('Service added');
    } catch (e: any) {
      toast.error(apiErrorMessage(e, 'Failed to add service'));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await memberProfileAPI.deleteService(id);
      setServices((prev) => prev.filter((s) => s.id !== id));
      toast.success('Service removed');
    } catch {
      toast.error('Failed to remove service');
    }
  };

  if (loading) return <p className="text-sm text-clinic-muted">Loading services…</p>;

  return (
    <div className="space-y-3">
      {services.length === 0 && !editing && (
        <p className="text-clinic-muted py-1">No services listed yet.</p>
      )}

      {services.map((s) => (
        <div
          key={s.id}
          className="flex items-start justify-between rounded-lg border border-clinic-primary-lighter p-3"
        >
          <div className="min-w-0">
            <p className="font-medium text-clinic-dark">
              {s.name}
              <span className="text-clinic-muted font-normal">
                {' · '}{s.duration_minutes} min
                {s.capacity > 1 ? ` · up to ${s.capacity} people` : ''}
                {formatPrice(s.price) ? ` · ${formatPrice(s.price)}` : ''}
              </span>
            </p>
            {s.description && <p className="text-sm text-clinic-muted mt-0.5">{s.description}</p>}
          </div>
          {editing && (
            <button
              type="button"
              onClick={() => handleDelete(s.id)}
              aria-label={`Delete ${s.name}`}
              className="ml-3 flex-shrink-0 p-1.5 text-clinic-muted hover:text-clinic-accent transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}

      {editing && (
        <div className="rounded-lg border border-dashed border-clinic-primary-lighter p-3 space-y-2">
          <Input
            placeholder="Service name (e.g. Initial Consult)"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block">
              <span className="text-xs text-clinic-muted">Duration</span>
              <select
                value={String(draft.duration_minutes)}
                onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 border border-clinic-primary-lighter rounded-input focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary text-clinic-dark bg-white text-sm"
              >
                {DURATION_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m} min</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-clinic-muted">Attendees</span>
              <select
                value={String(draft.capacity ?? 1)}
                onChange={(e) => setDraft({ ...draft, capacity: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 border border-clinic-primary-lighter rounded-input focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary text-clinic-dark bg-white text-sm"
              >
                <option value="1">1 (Individual)</option>
                <option value="2">2 (Couples)</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="6">6</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-clinic-muted">Price (optional)</span>
              <Input
                type="number"
                placeholder="$"
                className="mt-1"
                value={draft.price === null || draft.price === undefined ? '' : String(draft.price)}
                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              />
            </label>
          </div>
          <Input
            placeholder="Short description (optional)"
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <Button size="sm" onClick={handleAdd} isLoading={adding} leftIcon={<PlusIcon className="h-4 w-4" />}>
            Add Service
          </Button>
        </div>
      )}
    </div>
  );
};

export default ServicesEditor;
